import { Box, Button, Heading, Text, VStack } from "@chakra-ui/react"
import { useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import AddAddressForm from "../components/portfolio/AddAddressForm"
import { useUserDetails } from "../hooks/useAuthQueries"
import { useAddHome, useHomes } from "../hooks/usePortfolioQueries"

const AddProperty: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const portfolioId: number | undefined = (location.state as any)?.portfolioId
  const backPath: string = (location.state as any)?.from ?? "/portfolio"
  const backLabel = backPath === "/opportunities" ? "Back to Opportunities" : "Back to Portfolio"
  const addHomeMutation = useAddHome()
  const { data: homesData } = useHomes()
  const { data: userDetails } = useUserDetails()
  const homes = Array.isArray(homesData?.homes) ? homesData.homes : []
  const [error, setError] = useState<string | null>(null)

  const quotaLimit = userDetails?.data?.organisation?.homeQuota || 5
  const currentCount =
    userDetails?.data?.organisation?.totalHomes || homes.length
  const isQuotaReached = currentCount >= quotaLimit

  const handleSubmit = async (payload: any) => {
    if (isQuotaReached) {
      alert("You've reached your home quota limit.")
      navigate(backPath)
      return
    }

    // Clear any previous errors
    setError(null)

    try {
      const result = await addHomeMutation.mutateAsync(
        portfolioId ? { ...payload, portfolioId } : payload
      )
      console.log("Home added successfully", result)
      navigate(`/home/${result.data.home.id}`, { state: { from: backPath } })
    } catch (error: any) {
      console.error("Error adding home:", error)

      // Handle specific error cases
      if (error?.response?.data?.error_code === "INVALID_PROPERTY_TYPE") {
        setError(error.response.data.error)
      } else if (error?.response?.data?.error_code === "DUPLICATE_UPRN") {
        setError(error.response.data.error)
      } else {
        setError("There was an error adding the property. Please try again.")
      }
    }
  }

  if (isQuotaReached) {
    return (
      <Box p={8}>
        <VStack gap={6} align="stretch">
          <Box>
            <Button
              variant="ghost"
              onClick={() => navigate(backPath)}
              mb={4}
              alignSelf="flex-start"
            >
              ← {backLabel}
            </Button>
            <Heading size="xl">Add a Home</Heading>
          </Box>

          <Box
            bg="orange.50"
            border="1px solid"
            borderColor="orange.200"
            borderRadius="md"
            p={4}
          >
            <Text fontWeight="bold" color="orange.800">
              ⚠️ Home Quota Reached
            </Text>
            <Text fontSize="sm" color="orange.700" mt={1}>
              You've reached your limit of {quotaLimit} homes. Please delete a
              home from your portfolio before adding a new one.
            </Text>
          </Box>
        </VStack>
      </Box>
    )
  }

  return (
    <Box p={8}>
      <VStack gap={6} align="stretch">
        <Box>
          <Button
            variant="ghost"
            onClick={() => navigate(backPath)}
            mb={4}
            alignSelf="flex-start"
          >
            ← {backLabel}
          </Button>
          <Heading size="xl">Add a Home</Heading>
          <Text color="gray.600" mt={2}>
            Homes: {currentCount}/{quotaLimit}
          </Text>
        </Box>

        <AddAddressForm
          onSubmit={handleSubmit}
          isSubmitting={addHomeMutation.isPending}
          apiError={error}
          onClearApiError={() => setError(null)}
        />
      </VStack>
    </Box>
  )
}

export default AddProperty
